import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Eye, Pencil, Trash2, Sparkles, Plus } from "lucide-react";
import { fetchJobs, fetchCustomers, deleteJob, formatApiError } from "@/lib/api";
import { PageHeader, RangeToggle, StatusPill, useRange } from "@/components/dashboard/Primitives";
import SearchInput from "@/components/dashboard/SearchInput";
import JobDetailModal from "@/components/dashboard/JobDetailModal";
import JobFormDialog from "@/components/dashboard/JobFormDialog";
import DataSourceBadge from "@/components/dashboard/DataSourceBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Defensiv normalisering: true, "true", 1, "1", "yes" behandles som true.
const toBool = (v) => v === true || v === 1 || v === "true" || v === "1" || v === "yes";

const PURCHASE_ROW = {
    needs_purchase: { t: "🔴 Skal købes", c: "text-red-700" },
    ordered: { t: "🟡 Bestilt / på vej", c: "text-amber-700" },
    in_stock: { t: "🟢 På lager", c: "text-emerald-700" },
};

// Jobs come in automatically via ElevenLabs and MyServiceHelper's native
// integrations — this view is for overview, search and action.
export default function Jobs() {
    const [jobs, setJobs] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [range, setRange] = useRange("week");
    const [tab, setTab] = useState("active");
    const [search, setSearch] = useState("");
    const [openJob, setOpenJob] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editJob, setEditJob] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const refresh = () => {
        fetchJobs().then((d) => setJobs(d || [])).catch(() => {});
        fetchCustomers().then((d) => setCustomers(d || [])).catch(() => {});
    };

    // Deep-link support: /dashboard/jobs?job=JOB-ID opens that job once loaded.
    useEffect(() => {
        const jobId = searchParams.get("job");
        if (!jobId || openJob || !jobs.length) return;
        const match = jobs.find((j) => j.id === jobId);
        if (match) {
            setOpenJob(match);
            searchParams.delete("job");
            setSearchParams(searchParams, { replace: true });
        }
    }, [jobs, searchParams, openJob, setSearchParams]);

    useEffect(() => {
        refresh();
        // Live updates: poll every 30s + refresh when tab regains focus
        const interval = setInterval(refresh, 30000);
        const onFocus = () => refresh();
        const onVisibility = () => { if (document.visibilityState === "visible") refresh(); };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            clearInterval(interval);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    const filtered = useMemo(() => {
        const q = search.toLowerCase().trim();
        const DONE_STATUSES = ["afsluttet", "færdig", "done", "completed", "klar"];
        return jobs.filter((j) => {
            const isDone = DONE_STATUSES.includes((j.status || "").toLowerCase());
            const matchesTab = tab === "active" ? !isDone : isDone;
            if (!matchesTab) return false;
            if (!q) return true;
            return [j.id, j.customer, j.task, j.employee, j.status, j.priority, j.address, j.deadline]
                .some((v) => (v || "").toString().toLowerCase().includes(q));
        });
    }, [jobs, tab, search]);

    const jobDetails = useMemo(() => {
        const map = {};
        jobs.forEach((j) => { map[j.id] = j; });
        return map;
    }, [jobs]);

    const onDelete = async (id) => {
        if (!window.confirm(`Slet job ${id}?`)) return;
        try {
            await deleteJob(id);
            toast.success("Job slettet");
            refresh();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const openEdit = (j) => { setEditJob(j); setFormOpen(true); };

    return (
        <div data-testid="jobs-page" className="p-5 sm:p-8 max-w-7xl mx-auto">
            <PageHeader
                title="Jobs"
                subtitle="Jobs oprettes automatisk fra kundehenvendelser, ElevenLabs og MyServiceHelpers native integrationer."
                right={
                    <div className="flex items-center gap-3">
                        <DataSourceBadge entity="jobs" />
                        <RangeToggle value={range} onChange={setRange} testid="jobs-range" />
                        <Button data-testid="job-new-btn" onClick={() => { setEditJob(null); setFormOpen(true); }}
                            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white h-9">
                            <Plus className="w-4 h-4 mr-1" /> Ny opgave
                        </Button>
                    </div>
                }
            />

            <div className="mb-4 bg-blue-50/60 border border-blue-100 rounded-xl p-3.5 flex items-start gap-2.5 text-sm text-blue-900">
                <Sparkles className="w-4 h-4 mt-0.5 text-blue-600 shrink-0" />
                <p data-testid="jobs-info-banner">Jobs oprettes automatisk fra kundehenvendelser og gemmes direkte i MyServiceHelpers database.</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-hidden">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-slate-100 p-3">
                    <div className="flex">
                        {[{ v: "active", l: "Aktive" }, { v: "done", l: "Afsluttede" }].map((t) => (
                            <button
                                key={t.v}
                                data-testid={`jobs-tab-${t.v}`}
                                onClick={() => setTab(t.v)}
                                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${tab === t.v ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                            >{t.l}</button>
                        ))}
                    </div>
                    <SearchInput value={search} onChange={setSearch} placeholder="Søg ID, kunde, medarbejder, adresse, opgave, status…" testid="jobs-search" />
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                            <tr>
                                <th className="px-4 py-3 text-left">Job ID</th>
                                <th className="px-4 py-3 text-left">Kunde</th>
                                <th className="px-4 py-3 text-left">Opgave</th>
                                <th className="px-4 py-3 text-left">Adresse</th>
                                <th className="px-4 py-3 text-left">Prioritet</th>
                                <th className="px-4 py-3 text-left">Medarbejdere</th>
                                <th className="px-4 py-3 text-left">Tid</th>
                                <th className="px-4 py-3 text-left">Deadline</th>
                                <th className="px-4 py-3 text-left">Status</th>
                                <th className="px-4 py-3 text-right">Handling</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.map((j) => (
                                <tr key={j.id} data-testid={`job-row-${j.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => setOpenJob(j)}>
                                    <td className="px-4 py-3 font-mono text-slate-700">{j.id}</td>
                                    <td className="px-4 py-3 text-slate-900 font-medium">{j.customer || "Ukendt"}</td>
                                    <td className="px-4 py-3 text-slate-600">
                                        {j.task || "—"}
                                    </td>
                                    <td className="px-4 py-3 text-slate-500 max-w-[180px] truncate">{j.address || "—"}</td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full ${j.priority === "Høj" ? "bg-red-50 text-red-700" : j.priority === "Medium" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{j.priority || "—"}</span>
                                    </td>
                                    <td className="px-4 py-3 text-slate-700">
                                        {(() => {
                                            const list = Array.isArray(j.employees) && j.employees.length > 0 ? j.employees : (j.assigned_employee_name ? [j.assigned_employee_name] : (j.employee ? [j.employee] : []));
                                            if (list.length === 0) return <span className="text-slate-400">Ikke tildelt</span>;
                                            const signoff = (j.employee_signoff_status || "").toLowerCase();
                                            const isSignedOff = signoff && ["afmeldt", "frameldt", "signed_off", "off", "framelding", "signoff"].some((s) => signoff.includes(s));
                                            const badgeClass = isSignedOff
                                                ? "bg-red-50 text-red-700 border border-red-200"
                                                : "bg-emerald-50 text-emerald-700 border border-emerald-200";
                                            const badgeText = isSignedOff ? "Frameldt" : "Tilmeldt";
                                            return (
                                                <div className="flex flex-col gap-0.5" data-testid={`job-employee-cell-${j.id}`}>
                                                    <span className="text-sm">
                                                        {list[0]}
                                                        {list.length > 1 && <span className="text-[10px] text-slate-400 ml-1" title={list.join(", ")}>+{list.length - 1}</span>}
                                                    </span>
                                                    <span className={`inline-block w-fit text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${badgeClass}`} data-testid={`job-employee-status-${j.id}`}>{badgeText}</span>
                                                </div>
                                            );
                                        })()}
                                    </td>
                                    <td className="px-4 py-3 text-slate-700 font-mono text-xs" data-testid={`job-time-cell-${j.id}`}>
                                        {j.time_calculated_text || (j.time_hours ? `${Number(j.time_hours).toFixed(2)} t` : "—")}
                                    </td>
                                    <td className="px-4 py-3 text-slate-500">{j.deadline || "—"}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-col gap-1 items-start">
                                            <StatusPill status={j.status} />
                                            {toBool(j.purchase_required) && !toBool(j.customer_has_item) && j.purchase_status && PURCHASE_ROW[j.purchase_status] && (
                                                <span data-testid={`job-purchase-status-${j.id}`} className={`text-[11px] font-semibold ${PURCHASE_ROW[j.purchase_status].c}`}>
                                                    {PURCHASE_ROW[j.purchase_status].t}
                                                </span>
                                            )}
                                            {j.purchase_status === "in_stock" && j.inventory_location && (
                                                <span data-testid={`job-inventory-loc-${j.id}`} className="text-[10px] text-slate-500 max-w-[160px] truncate" title={`Lagerplacering: ${j.inventory_location}`}>
                                                    📦 {j.inventory_location}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex justify-end gap-1.5">
                                            <Button size="sm" variant="outline" data-testid={`job-view-${j.id}`} onClick={() => setOpenJob(j)} className="h-8 text-xs">
                                                <Eye className="w-3.5 h-3.5 mr-1" /> Se
                                            </Button>
                                            <Button size="sm" variant="outline" data-testid={`job-edit-${j.id}`} onClick={() => openEdit(j)} className="h-8 text-xs">
                                                <Pencil className="w-3.5 h-3.5 mr-1" /> Redigér
                                            </Button>
                                            <Button size="sm" variant="outline" data-testid={`job-delete-${j.id}`} onClick={() => onDelete(j.id)} className="h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">{search ? "Ingen jobs matcher søgningen." : "Ingen data for valgt periode"}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <JobDetailModal job={openJob} detail={openJob ? jobDetails[openJob.id] : null} open={!!openJob} onClose={() => setOpenJob(null)} />
            <JobFormDialog open={formOpen} job={editJob} customers={customers} onClose={() => setFormOpen(false)} onSaved={refresh} />
        </div>
    );
}
